// Console Team: who can sign in, how much of the console they reach, which
// clients, and the invitations (149 and 150).
//
// EVERY CALL HERE IS AN ADMIN'S, AND THE SERVICE SAYS SO, NOT THIS FILE. The
// routes take requireAdminSession; an Editor or a Viewer who reaches the Team
// screen by a deep link is answered nova_admin_required and the screen draws the
// service's sentence. A second copy of that rule here would be the one that
// drifts.
//
// THE LIST IS READ IN THE ORDER IT IS ASKED FOR (12 Oct hand-off: every column
// sorts, on the server). Nothing here compares two rows.

import { PEOPLE_ENDPOINTS, type TeamSortKey } from "../../../api/src/admin/endpoints.js";
import { adminRefusalFor, type AdminRefusal, type Attempt } from "./admin.js";
import { authenticated, type ApiOptions } from "./client.js";
import type { SessionToken } from "../session/store.js";

export type { TeamSortKey };

export type Access = "admin" | "editor" | "viewer";

/**
 * A client by name. `archived` is a company an Editor or a Viewer still holds
 * after it was archived: the holding stands (010 revokes nothing on archive),
 * and it is drawn as held, never as a choice.
 */
export type TeamClient = { readonly clientId: string; readonly name: string; readonly archived: boolean };

/** The clients a person holds that are live, which are the only ones a control may send. */
export function liveHeld(person: { readonly clients: "all" | readonly TeamClient[] }): readonly TeamClient[] {
  return person.clients === "all" ? [] : person.clients.filter((k) => !k.archived);
}

/** One show a Viewer was granted, and the grant that revokes it. */
export type TeamShow = { readonly grantId: string; readonly activationId: string };

export type TeamPerson = {
  readonly identityId: string;
  readonly email: string;
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly access: Access;
  /** "ba" for a Viewer who has used the BA floor app. */
  readonly kind: "console" | "ba";
  /** A Viewer's one company. Null for Nova staff. */
  readonly homeClientId: string | null;
  /** "all" for an Admin; an Editor's clients; a Viewer's one company. */
  readonly clients: "all" | readonly TeamClient[];
  readonly canExport: boolean;
  /** A confirmed authenticator on the account. */
  readonly secondFactor: boolean;
  readonly lastSeenAt: string | null;
  /** "invited" until they first sign in. Invites do not expire. */
  readonly status: "active" | "invited";
  /** When an Admin last sent the invitation mail, or null if nobody has. */
  readonly invitedAt: string | null;
  /** A Viewer's shows, granted one at a time. */
  readonly shows: readonly TeamShow[];
  /** The Admin reading the list. */
  readonly you: boolean;
};

export type Team = { readonly people: readonly TeamPerson[]; readonly clients: readonly TeamClient[] };

export type TeamOrder = { readonly key: TeamSortKey; readonly dir: "asc" | "desc" };

const fields = (body: unknown): Record<string, unknown> =>
  typeof body === "object" && body !== null ? (body as Record<string, unknown>) : {};

const text = (value: unknown): string | null => (typeof value === "string" && value !== "" ? value : null);

const at = (template: string, identityId: string): string =>
  template.replace(":identityId", encodeURIComponent(identityId));

function unreadable(what: string): AdminRefusal {
  return {
    kind: "unreachable",
    detail:
      `The service answered with something this console cannot read as ${what}. It may well have worked, ` +
      "so read the list again before doing it a second time.",
  };
}

function clientOf(value: unknown): TeamClient | null {
  const row = fields(value);
  const clientId = text(row["client_id"]);
  const name = typeof row["name"] === "string" ? row["name"] : null;
  return clientId === null || name === null ? null : { clientId, name, archived: row["archived"] === true };
}

/** One person off the wire, or null where any field is not what the service promises. */
export function personFrom(value: unknown): TeamPerson | null {
  const row = fields(value);
  const identityId = text(row["identity_id"]);
  const email = text(row["email"]);
  const access = row["access"] === "admin" || row["access"] === "editor" || row["access"] === "viewer" ? row["access"] : null;
  const kind = row["kind"] === "ba" ? "ba" : row["kind"] === "console" ? "console" : null;
  const status = row["status"] === "invited" ? "invited" : row["status"] === "active" ? "active" : null;
  if (identityId === null || email === null || access === null || kind === null || status === null) return null;

  let clients: "all" | TeamClient[];
  if (row["clients"] === "all") clients = "all";
  else if (Array.isArray(row["clients"])) {
    clients = [];
    for (const entry of row["clients"]) {
      const client = clientOf(entry);
      if (client === null) return null;
      clients.push(client);
    }
  } else return null;

  const shows: TeamShow[] = [];
  // Absent is a service from before 150's shows, and reads as none rather than
  // as a refusal of the whole list.
  if (Array.isArray(row["shows"])) {
    for (const entry of row["shows"]) {
      const show = fields(entry);
      const grantId = text(show["grant_id"]);
      const activationId = text(show["activation_id"]);
      if (grantId === null || activationId === null) return null;
      shows.push({ grantId, activationId });
    }
  }

  return {
    identityId,
    email,
    firstName: text(row["first_name"]),
    lastName: text(row["last_name"]),
    access,
    kind,
    homeClientId: text(row["home_client_id"]),
    clients,
    canExport: row["can_export"] === true,
    secondFactor: row["second_factor"] === true,
    lastSeenAt: text(row["last_seen_at"]),
    status,
    invitedAt: text(row["invited_at"]),
    shows,
    you: row["you"] === true,
  };
}

/** The Team list, in the order asked for, or the service's own order for null. */
export async function readTeam(
  options: ApiOptions,
  token: SessionToken,
  order: TeamOrder | null = null,
): Promise<Attempt<Team>> {
  const query = order === null ? "" : `?sort=${encodeURIComponent(order.key)}&dir=${order.dir === "desc" ? "desc" : "asc"}`;
  const answer = await authenticated(options, token, { path: `${PEOPLE_ENDPOINTS.team}${query}`, method: "GET" });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };

  const body = fields(answer.body);
  if (!Array.isArray(body["people"]) || !Array.isArray(body["clients"])) {
    return { ok: false, ...unreadable("the Team list") };
  }
  const people: TeamPerson[] = [];
  for (const entry of body["people"]) {
    const person = personFrom(entry);
    if (person === null) return { ok: false, ...unreadable("the Team list") };
    people.push(person);
  }
  const clients: TeamClient[] = [];
  for (const entry of body["clients"]) {
    const client = clientOf(entry);
    if (client === null) return { ok: false, ...unreadable("the Team list") };
    clients.push(client);
  }
  return { ok: true, value: { people, clients } };
}

async function personWrite(
  options: ApiOptions,
  token: SessionToken,
  request: { path: string; method: "POST" | "PUT"; json: unknown },
): Promise<Attempt<TeamPerson>> {
  const answer = await authenticated(options, token, request);
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };
  const person = personFrom(answer.body);
  return person === null ? { ok: false, ...unreadable("a person on Team") } : { ok: true, value: person };
}

/** What an Admin fills in to invite somebody. */
export type Invitee = {
  readonly email: string;
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly access: Access;
  /** An Editor's clients. */
  readonly clientIds: readonly string[];
  /** A Viewer's one company. */
  readonly clientId: string | null;
  readonly canExport: boolean;
};

const ROLE: Readonly<Record<Access, string>> = { admin: "nova_admin", editor: "nova_editor", viewer: "client_viewer" };

/**
 * Put the person on the books (POST identities). Returns their id; the
 * invitation mail is a second call, so a person whose mail did not go is on
 * Team as Invited with nothing sent, and Resend is the way to finish.
 */
export async function createTeamMember(
  options: ApiOptions,
  token: SessionToken,
  invitee: Invitee,
): Promise<Attempt<{ readonly identityId: string }>> {
  const answer = await authenticated(options, token, {
    path: PEOPLE_ENDPOINTS.createIdentity,
    method: "POST",
    json: {
      email: invitee.email,
      role: ROLE[invitee.access],
      client_id: invitee.access === "viewer" ? invitee.clientId : null,
      ...(invitee.access === "editor" ? { client_ids: invitee.clientIds } : {}),
      ...(invitee.access === "admin" ? {} : { can_export: invitee.canExport }),
      first_name: invitee.firstName,
      last_name: invitee.lastName,
    },
  });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };
  const identityId = text(fields(answer.body)["identity_id"]);
  return identityId === null ? { ok: false, ...unreadable("a new person") } : { ok: true, value: { identityId } };
}

/** Send, or send again, the invitation mail. Refused once they have signed in. */
export function sendInvitation(options: ApiOptions, token: SessionToken, identityId: string): Promise<Attempt<TeamPerson>> {
  return personWrite(options, token, { path: at(PEOPLE_ENDPOINTS.identityInvitation, identityId), method: "POST", json: {} });
}

/** Change somebody's access. A Viewer needs their one company; an Editor may be given clients. Signs them out everywhere. */
export function changeAccess(
  options: ApiOptions,
  token: SessionToken,
  identityId: string,
  to: { readonly access: Access; readonly clientId: string | null; readonly clientIds: readonly string[] | null },
): Promise<Attempt<TeamPerson>> {
  return personWrite(options, token, {
    path: at(PEOPLE_ENDPOINTS.identityAccess, identityId),
    method: "PUT",
    json: {
      access: to.access,
      ...(to.access === "viewer" ? { client_id: to.clientId } : {}),
      ...(to.access === "editor" && to.clientIds !== null ? { client_ids: to.clientIds } : {}),
    },
  });
}

/** An Editor's clients, as the whole list. */
export function setEditorClients(
  options: ApiOptions,
  token: SessionToken,
  identityId: string,
  clientIds: readonly string[],
): Promise<Attempt<TeamPerson>> {
  return personWrite(options, token, {
    path: at(PEOPLE_ENDPOINTS.identityClients, identityId),
    method: "PUT",
    json: { client_ids: clientIds },
  });
}

/** The export switch. An Admin always exports, and the service refuses the switch for one. */
export function setExport(
  options: ApiOptions,
  token: SessionToken,
  identityId: string,
  canExport: boolean,
): Promise<Attempt<TeamPerson>> {
  return personWrite(options, token, {
    path: at(PEOPLE_ENDPOINTS.identityExport, identityId),
    method: "PUT",
    json: { can_export: canExport },
  });
}

/** Remove somebody, or cancel their invite: the account is disabled, and nothing re-enables it. */
export async function removeTeamMember(
  options: ApiOptions,
  token: SessionToken,
  identityId: string,
): Promise<Attempt<{ readonly identityId: string }>> {
  const answer = await authenticated(options, token, {
    path: at(PEOPLE_ENDPOINTS.disableIdentity, identityId),
    method: "POST",
    json: {},
  });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };
  return { ok: true, value: { identityId } };
}

/** Grant a Viewer one show of their company. */
export async function grantShow(
  options: ApiOptions,
  token: SessionToken,
  viewerId: string,
  activationId: string,
): Promise<Attempt<{ readonly grantId: string }>> {
  const answer = await authenticated(options, token, {
    path: PEOPLE_ENDPOINTS.createGrant,
    method: "POST",
    json: { viewer_id: viewerId, activation_id: activationId },
  });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };
  const grantId = text(fields(answer.body)["grant_id"]);
  return grantId === null ? { ok: false, ...unreadable("a grant") } : { ok: true, value: { grantId } };
}

/** Take one show back. */
export async function revokeShow(
  options: ApiOptions,
  token: SessionToken,
  grantId: string,
): Promise<Attempt<{ readonly grantId: string }>> {
  const answer = await authenticated(options, token, {
    path: PEOPLE_ENDPOINTS.revokeGrant.replace(":grantId", encodeURIComponent(grantId)),
    method: "POST",
    json: {},
  });
  if (!answer.ok) return { ok: false, ...adminRefusalFor(answer) };
  return { ok: true, value: { grantId } };
}
